import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import {
  FaInstagram,
  FaTiktok,
  FaTwitch,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import { AlertTriangle, Briefcase } from "lucide-react";
import {
  Gamepad2,
  HeartHandshake,
  Loader2,
  Sparkles,
  Trophy,
  Workflow,
} from "lucide-react";
import { auth, db } from "../Context/firebaseConfig";
import useEntitlements from "../hooks/useEntitlements";
import EntitlementGate from "./shared/EntitlementGate";
import { normalizeSocialUrl } from "../utils/socialLinks";

const MINT = "#10b981";
const PURPLE = "#a855f7";

const QUESTION_CARDS = [
  {
    key: "questionOne",
    icon: Workflow,
    accent: MINT,
    label: "What things would make the editing gig the easiest for you?",
  },
  {
    key: "questionTwo",
    icon: HeartHandshake,
    accent: PURPLE,
    label: "How can you make the editing gig easiest for the other person?",
  },
  {
    key: "questionThree",
    icon: AlertTriangle,
    accent: "#f59e0b",
    label: "What are the pain points others may have while working with you?",
  },
  {
    key: "questionFour",
    icon: Trophy,
    accent: MINT,
    label: "What do you bring to the table?",
  },
];

const SOCIALS = [
  { key: "youtube", label: "YouTube", icon: FaYoutube },
  { key: "twitch", label: "Twitch", icon: FaTwitch },
  { key: "instagram", label: "Instagram", icon: FaInstagram },
  { key: "twitter", label: "X / Twitter", icon: FaTwitter },
  { key: "tiktok", label: "TikTok", icon: FaTiktok },
];

const CREATOR_UPGRADE_CARDS = [
  {
    key: "reusableEditingInstructions",
    icon: Workflow,
    title: "Reusable instruction templates",
    description: "Keep your pacing, caption, music, and delivery notes ready for future projects.",
  },
  {
    key: "projectOrganization",
    icon: Sparkles,
    title: "Project organization",
    description: "Organize editing needs, project notes, and creator workflow details in one place.",
  },
  {
    key: "batchProjectPlanning",
    icon: Gamepad2,
    title: "Batch order creation",
    description: "Prepare multiple uploads or clips before sending work to an editor.",
  },
  {
    key: "contentWorkflowBoard",
    icon: HeartHandshake,
    title: "Content workflow board",
    description: "See upcoming content, editing needs, and order planning in one place.",
  },
  {
    key: "discountedCreatorReview",
    icon: Trophy,
    title: "Discounted creator review",
    description: "Get discounted hands-on feedback for your channel, workflow, and editing needs.",
  },
];

function SocialButton({ href, label, icon: Icon, platform }) {
  const socialUrl = normalizeSocialUrl(platform, href);
  if (!socialUrl) return null;

  return (
    <a
      href={socialUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-white/65 transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.07] hover:text-white"
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}

function QuestionCard({ config, body }) {
  const Icon = config.icon;
  if (!body) return null;

  return (
    <div
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/[0.05] sm:p-6"
      style={{ boxShadow: `0 20px 60px -42px ${config.accent}` }}
    >
      <div
        className="pointer-events-none absolute -right-14 -top-14 h-40 w-40 rounded-full opacity-25 blur-3xl transition group-hover:opacity-40"
        style={{ background: config.accent }}
      />
      <div className="relative flex items-start gap-4">
        <span
          className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border"
          style={{
            color: config.accent,
            borderColor: `${config.accent}55`,
            background: `${config.accent}18`,
          }}
        >
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold leading-snug text-white">
            {config.label}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-white/60">{body}</p>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="grid gap-4">
        <div className="h-72 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="h-36 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
          <div className="h-36 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
        </div>
      </div>
    </section>
  );
}

function UnlockedCreatorTool({ icon: Icon, title, description }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div
        className="mb-4 grid h-11 w-11 place-items-center rounded-2xl border"
        style={{
          color: MINT,
          borderColor: `${MINT}55`,
          background: `${MINT}18`,
        }}
      >
        <Icon size={18} />
      </div>
      <h3 className="text-base font-bold text-white">{title} unlocked</h3>
      <p className="mt-2 text-sm leading-relaxed text-white/60">{description}</p>
    </div>
  );
}

function CreatorUpgradeTools({ entitlements }) {
  return (
    <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6">
      <div className="mb-5">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40">
          <Sparkles size={13} style={{ color: MINT }} />
          Creator upgrades
        </div>
        <h2 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Workflow Tools
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/50">
          These are preview cards only for now. Core creator flows, YouTube connection,
          messaging, orders, and profile viewing remain free.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CREATOR_UPGRADE_CARDS.map((item) => {
          const Icon = item.icon;
          return (
            <EntitlementGate
              key={item.key}
              allowed={Boolean(entitlements[item.key])}
              title={item.title}
              description={item.description}
              cta="View pricing"
            >
              <UnlockedCreatorTool
                icon={Icon}
                title={item.title}
                description={item.description}
              />
            </EntitlementGate>
          );
        })}
      </div>
    </section>
  );
}

export default function CreatorProfile() {
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState(null);
  const { entitlements } = useEntitlements(creator);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setError("No user is logged in");
        return;
      }

      try {
        const docRef = doc(db, "Users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setCreator({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError("No creator found with that ID");
        }
      } catch (err) {
        setError("Failed to fetch data or insufficient permissions");
        console.error("Error fetching creator profile:", err);
      }
    });

    return () => unsubscribe();
  }, []);

  if (error) {
    return (
      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="rounded-3xl border border-red-400/20 bg-red-500/10 p-5 text-sm text-red-100">
          {error}
        </div>
      </section>
    );
  }

  if (!creator) {
    return <LoadingState />;
  }

  const profile = creator.profileInfo || {};
  const questions = profile.questions || {};
  const visibleQuestions = QUESTION_CARDS.filter((item) => questions[item.key]);

  return (
    <>
      <section className="relative mx-auto max-w-6xl px-4 pb-10 pt-8 sm:px-6 sm:pt-12">
        <div
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl sm:p-8 lg:p-10"
          style={{ boxShadow: `0 24px 90px -50px ${PURPLE}` }}
        >
          <div
            className="pointer-events-none absolute -left-28 -top-28 h-72 w-72 rounded-full opacity-25 blur-3xl"
            style={{ background: PURPLE }}
          />
          <div
            className="pointer-events-none absolute -bottom-28 right-0 h-72 w-72 rounded-full opacity-20 blur-3xl"
            style={{ background: MINT }}
          />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-10">
            <div className="flex flex-col items-center text-center lg:w-[280px] lg:items-start lg:text-left">
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-full opacity-70 blur-2xl"
                  style={{
                    background: `conic-gradient(from 120deg, ${PURPLE}, ${MINT}, ${PURPLE})`,
                  }}
                />
                <div
                  className="relative rounded-full p-1"
                  style={{ background: `linear-gradient(135deg, ${PURPLE}, ${MINT})` }}
                >
                  <img
                    src={profile.profilePicUrl}
                    alt={`${profile.username || "Creator"} profile`}
                    className="h-28 w-28 rounded-full bg-[#0d0a18] object-cover sm:h-36 sm:w-36"
                    fetchPriority="high"
                    decoding="async"
                    loading="eager"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                {profile.role && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/70">
                    <Briefcase size={12} style={{ color: PURPLE }} />
                    {profile.role}
                  </span>
                )}
                {profile.mainGame && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-100">
                    <Gamepad2 size={12} />
                    {profile.mainGame}
                  </span>
                )}
              </div>
            </div>

            <div className="min-w-0 flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40 lg:justify-start">
                <Sparkles size={13} style={{ color: MINT }} />
                Creator Portfolio
              </div>
              <h1 className="mt-2 truncate text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
                @{profile.username || "creator"}
              </h1>
              {profile.tagline && (
                <p className="mt-3 text-base font-medium leading-relaxed text-white/70 sm:text-lg">
                  {profile.tagline}
                </p>
              )}
              {profile.about && (
                <div className="mt-5 rounded-3xl border border-white/10 bg-black/25 p-5 text-left backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/40">
                    About
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-white/65">
                    {profile.about}
                  </p>
                </div>
              )}
              <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                {SOCIALS.map((item) => (
                  <SocialButton
                    key={item.key}
                    href={creator.socials?.[item.key]}
                    label={item.label}
                    icon={item.icon}
                    platform={item.key}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CreatorUpgradeTools entitlements={entitlements} />

      {visibleQuestions.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6">
          <div className="mb-5">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Working Together
            </h2>
            <p className="mt-1 text-sm text-white/50">
              The context editors need before starting a project.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {visibleQuestions.map((item) => (
              <QuestionCard
                key={item.key}
                config={item}
                body={questions[item.key]}
              />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
